import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import type { DuplicateCleanupConfig } from 'webapp/utils/html';

import willDestroy from 'webapp/modifiers/will-destroy';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import type { GoalsBaseArgs } from 'webapp/components/goals/base';
import GoalsBaseComponent from 'webapp/components/goals/base';
import GoalsStrategyTableRowComponent from 'webapp/components/goals/strategy/table-row';
import { TopStrategyItemId as TreeTopStrategyItemId } from 'webapp/components/goals/tree/index';
import TableAddNewItemComponent from 'webapp/components/table/add-new-item';

import type { Goal } from 'webapp/models-custom';
import type { DeleteAction } from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import FilterSortGoalOKRsInList from 'webapp/models-custom/filter-sort/goal-okrs-in-list';

import type { TreeItemData as BaseTreeItemData } from 'webapp/services/model-manager/goals';

interface Args extends GoalsBaseArgs {
	addNew: () => void;
	onFilterSortChange: (filterSortResults: Goal[]) => void;
}

interface TreeItemData extends BaseTreeItemData {
	goal: Goal;
	depth: number;
}

export default class GoalsStrategyTableComponent extends GoalsBaseComponent<Args> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	filterSort: FilterSortGoalOKRsInList;
	batchSelect: BatchSelect<Goal>;

	@tracked orderedGoalData: TreeItemData[] = [];
	@tracked expandedIds: number[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = new FilterSortGoalOKRsInList({
			getAllItems: () => {
				return this.allGoals;
			},
			afterFilter: () => {
				this.args.onFilterSortChange(this.filterSort.results);
				this.setOrderedGoalData();
			},
			onOpenView: (viewId: string) => {
				this.args.openView(viewId);
			},
		});
		this.filterSort = filterSort;

		this.batchSelect = new BatchSelect<Goal>({
			getAllItems: () => {
				return this.allVisibleGoals;
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				return this.doBatchAction(actionName as DeleteAction);
			},
		});

		this.setOrderedGoalData();
	}

	get allGoals() {
		return this.args.model;
	}

	get resultGoals() {
		return this.filterSort.results;
	}

	@cached
	get allVisibleGoals() {
		// do not include subgoals that arent expanded / visible
		return this.orderedGoalData
			.filter((goalData) => {
				const parentId = goalData.parentId;
				return parentId === TreeTopStrategyItemId || this.expandedIds.includes(parentId);
			})
			.map((goalData) => goalData.goal);
	}

	@cached
	get isFavoriteSectionVisible(): boolean {
		return this.favoriteOrderedGoalData.length > 0;
	}

	get favoriteOrderedGoalData(): TreeItemData[] {
		return this.getOrderedGoalDataByFavorite(true);
	}

	get nonFavoriteOrderedGoalData(): TreeItemData[] {
		return this.getOrderedGoalDataByFavorite(false);
	}

	get duplicateCleanupConfig(): DuplicateCleanupConfig {
		return {
			sectionSelectors: ['.favorites-section', '.non-favorites-section'],
			itemSelector: 'tr.table-row[data-id]',
			shouldKeepInSection: (itemId: string, sectionSelector: string): boolean => {
				const goal = this.allGoals.find((goal: Goal) => goal.id === itemId);
				if (!goal) {
					return false;
				}
				return (
					(goal.favorite && sectionSelector === '.favorites-section') ||
					(!goal.favorite && sectionSelector === '.non-favorites-section')
				);
			},
		};
	}

	get isUserSettingOverviewHidden(): boolean {
		return this.userSettingsService.isOkrOverviewHidden;
	}

	@action
	toggleExpanded(id: number) {
		if (this.expandedIds.includes(id)) {
			const descendantIds = this.mmGoalsService.getDescendantIds(id, this.orderedGoalData);
			this.expandedIds = this.expandedIds.filter(
				(expandedId) => expandedId !== id && !descendantIds.includes(expandedId),
			);
		} else {
			this.expandedIds = [...this.expandedIds, id];
		}
	}

	@action
	toggleOverview(): void {
		void this.userSettingsService.save(
			UserSettingsKey.OKR_OVERVIEW_HIDDEN,
			!this.isUserSettingOverviewHidden,
		);
	}

	@action
	teardownView(): void {
		this.args.onFilterSortChange([]);
	}

	private setOrderedGoalData() {
		const treeData = this.mmGoalsService.generateTreeDataForGoals(this.resultGoals);
		this.orderedGoalData = treeData.filter((goalData) => !!goalData.goal);
	}

	private getOrderedGoalDataByFavorite(isFavorite: boolean): TreeItemData[] {
		const subtrees: TreeItemData[][] = [];
		let currentSubtree: TreeItemData[] = [];

		for (const item of this.orderedGoalData) {
			if (item.depth === 0) {
				if (currentSubtree.length) {
					subtrees.push(currentSubtree);
				}
				currentSubtree = [item];
			} else {
				currentSubtree.push(item);
			}
		}
		if (currentSubtree.length) {
			subtrees.push(currentSubtree);
		}

		return subtrees
			.filter((subtree) => {
				const first = subtree[0];
				return first?.goal.favorite === isFavorite;
			})
			.sort((a, b) => {
				const aIsObjective = a[0]?.goal.isObjectiveType;
				const bIsObjective = b[0]?.goal.isObjectiveType;
				if (aIsObjective === bIsObjective) return 0;
				return aIsObjective ? -1 : 1;
			})
			.flat();
	}
	<template>
		<div class="bottom-padding" {{willDestroy this.teardownView}}>
			<FilterLineProxyComponent @model={{this.filterSort}}>
				{{#if this.isUserSettingOverviewHidden}}
					<div
						class="button theme-grey-empty"
						role="button"
						{{on "click" this.toggleOverview}}
					>
						<CommonUiIconComponent @name="EyeOpen" @size="sm" />Show overview
					</div>
				{{/if}}
			</FilterLineProxyComponent>

			<FilterNoResultsComponent @model={{this.filterSort}} />

			{{! Favorite goals section }}
			<table
				class="goals-list flexible-table bigtable favorites-section
					{{if this.filterSort.noResults 'hide'}}"
			>
				<colgroup>
					<col class="cc keep-width" />
					<col class="keep-width" width="30%" />
					<col />
					<col width="8%" />
					<col width="140" />
					<col width="80" />
					<col />
				</colgroup>
				<thead class={{unless this.isFavoriteSectionVisible "hidden"}}>
					<tr>
						<th></th>
						<th>Title</th>
						<th>Progress</th>
						<th class="prio-low">Timeframe</th>
						<th class="prio-low">Status</th>
						<th class="prio-low">Owner</th>
						<th class="button-ct"></th>
					</tr>
				</thead>
				<tbody>
					{{#each this.favoriteOrderedGoalData as |goalData|}}
						<GoalsStrategyTableRowComponent
							@model={{goalData.goal}}
							@parentId={{goalData.parentId}}
							@depth={{goalData.depth}}
							@expandedIds={{this.expandedIds}}
							@toggleExpanded={{this.toggleExpanded}}
							@batchSelect={{this.batchSelect}}
						/>
					{{/each}}
				</tbody>
			</table>

			{{! Non-favorite goals section }}
			<table
				class="goals-list flexible-table bigtable non-favorites-section
					{{if this.filterSort.noResults 'hide'}}"
			>
				<colgroup>
					<col class="cc keep-width" />
					<col class="keep-width" width="30%" />
					<col />
					<col width="8%" />
					<col width="140" />
					<col width="80" />
					<col />
				</colgroup>
				<thead class={{if this.isFavoriteSectionVisible "hidden"}}>
					<tr>
						<th></th>
						<th>Title</th>
						<th>Progress</th>
						<th class="prio-low">Timeframe</th>
						<th class="prio-low">Status</th>
						<th class="prio-low">Owner</th>
						<th class="button-ct"></th>
					</tr>
				</thead>
				<tbody>
					{{#each this.nonFavoriteOrderedGoalData as |goalData|}}
						<GoalsStrategyTableRowComponent
							@model={{goalData.goal}}
							@parentId={{goalData.parentId}}
							@depth={{goalData.depth}}
							@expandedIds={{this.expandedIds}}
							@toggleExpanded={{this.toggleExpanded}}
							@batchSelect={{this.batchSelect}}
						/>
					{{/each}}
				</tbody>
			</table>

			<TableAddNewItemComponent
				@title="New goal"
				@addNewItem={{@addNew}}
				@model={{this.filterSort}}
			/>

		</div>

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
